/**
 * The Revenue page's lifecycle, leakage and orders routes (wave 18 stage B):
 * `GET /analytics/revenue/lifecycle`, `/leakage` and `/orders` (+ `format=csv`).
 *
 * The contract is `api/src/competition_insights/modules/revenue/README.md` *Stage B*, and
 * `api/tests/modules/revenue/revenue_leakage_openapi.json` pins the wire shapes below.
 * Money arrives in minor units of `meta.currency` and leaves in major units of the same
 * currency, as the Overview's does — except an order row's, which is in the order's own
 * currency (`currency` on the row). Every null keeps its reason; nothing unknown becomes 0.
 */

import { majorUnits } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import type { CsvFile, SortOrder } from './list'
import { overviewSearch } from './overview'
import { revenueCompare, type PreviousUnavailable, type RevenueBasis, type RevenueMetaOut } from './revenue'
import type { Envelope } from './types'

// --- vocabulary ---------------------------------------------------------------------------

/** The page's basis: gross revenue, or net of refunds (`api/revenue.ts`'s). */
export type LeakageBasis = RevenueBasis
export type LifecycleAlign = 'launch' | 'draw'
export type LifecycleUnavailableReason = 'not_found' | 'no_start_date' | 'no_draw_date'
export type LeakageNullReason =
  | 'no_revenue'
  | 'too_few_refunds'
  | 'no_coupon_source'
  | 'needs_payment_events'
  | 'no_failures'
  | 'no_valued_failures'

/** The most competitions one lifecycle chart compares (the API's limit, and the chart's colours). */
export const LIFECYCLE_MAX_COMPETITIONS = 6

export const ORDER_SORTS = ['completed_at', 'gross', 'net', 'refunded', 'tickets'] as const
export type OrderSort = (typeof ORDER_SORTS)[number]
export const DEFAULT_ORDER_SORT: OrderSort = 'completed_at'

/** The credited touch's 10-channel key, or no touch credited, or a touch not classified yet. */
export const ORDER_CHANNELS = [
  'meta_paid',
  'facebook_organic',
  'instagram_organic',
  'google_paid',
  'google_organic',
  'email',
  'push',
  'referral',
  'direct',
  'other',
  'unattributed',
  'unclassified',
] as const
export type OrderChannel = (typeof ORDER_CHANNELS)[number]

export const ORDER_CUSTOMER_TYPES = ['new', 'returning', 'guest'] as const
export type OrderCustomerType = (typeof ORDER_CUSTOMER_TYPES)[number]

export const ATTRIBUTION_MODELS = ['last_non_direct', 'last_touch', 'first_touch', 'paid_click'] as const
export type OrderAttributionModel = (typeof ATTRIBUTION_MODELS)[number]

/** Rows per Orders page (the API's default; at most 200). */
export const ORDERS_PAGE_SIZE = 50
export const ORDERS_MAX_LIMIT = 200
/** The most rows one Orders CSV carries (`orders.py` `CSV_MAX_ROWS`). */
export const ORDERS_CSV_MAX_ROWS = 10_000

export const REVENUE_LEAKAGE_PATHS = {
  lifecycle: '/analytics/revenue/lifecycle',
  leakage: '/analytics/revenue/leakage',
  orders: '/analytics/revenue/orders',
} as const

/** The name the API gives the Orders file, for an answer that names none. */
export function ordersCsvFilename(from: string, to: string): string {
  return `revenue-orders-${from}-to-${to}.csv`
}

// --- queries --------------------------------------------------------------------------------

export interface LifecycleQuery {
  filters: GlobalFilters
  basis: LeakageBasis
  /** 1 to 6, in the order the chart colours them. */
  competitionIds: readonly string[]
  align: LifecycleAlign
}

export interface LeakageQuery {
  filters: GlobalFilters
  basis: LeakageBasis
}

export interface OrdersFilters {
  competitionId?: string
  channel?: OrderChannel
  model?: OrderAttributionModel
  paymentProvider?: string
  customerType?: OrderCustomerType
  /** Gross at least this, in minor units of the order's own currency (the API compares recorded amounts). */
  minGrossMinor?: number
}

export interface OrdersQuery extends OrdersFilters {
  filters: GlobalFilters
  sort: OrderSort
  order: SortOrder
  /** `next_cursor` of the previous page; the JSON route only. */
  cursor?: string
  /** 1–200; the JSON route only. */
  limit?: number
}

// --- wire shapes (snake_case, as the API sends them) ---------------------------------------

/** `schemas.RevenueMeta`: one meta on all seven revenue routes (`api/revenue.ts`'s). */
export type RevenueLeakageMetaOut = RevenueMetaOut

export interface LifecyclePointOut {
  day: number
  date: string
  revenue: number
  tickets: number
  cumulative_revenue: number
  cumulative_sold_fraction: number | null
}

export interface LifecycleCompetitionOut {
  competition_id: string
  name: string
  category: string | null
  start_at: string
  draw_at: string | null
  draw_source: 'draw_at' | 'end_at' | null
  capacity: number | null
  sold_out_day: number | null
  points: LifecyclePointOut[]
  truncated: boolean
  totals: { revenue: number; tickets: number }
}

export interface RevenueLifecycleOut {
  data: {
    basis: LeakageBasis
    align: LifecycleAlign
    competitions: LifecycleCompetitionOut[]
    not_available: { competition_id: string; reason: LifecycleUnavailableReason }[]
    clipped_by: 'competition_dates'
  }
  meta: RevenueLeakageMetaOut
}

interface AmountCountOut {
  amount: number
  count: number
}

export interface RevenueLeakageOut {
  data: {
    basis: LeakageBasis
    compare: RevenueLeakageMetaOut['compare']
    revenue_gross: number
    refunds: {
      amount: number
      count: number
      rate: number | null
      rate_null_reason: LeakageNullReason | null
      median_lag_days: number | null
      median_lag_null_reason: LeakageNullReason | null
      lag_refunds: number
      by_reason: (AmountCountOut & { reason_category: string })[]
      by_day: (AmountCountOut & { day: string })[]
      previous_amount: number | null
      previous_rate: number | null
      previous_rate_null_reason: LeakageNullReason | null
    }
    discounts: {
      amount: number
      share_of_gross: number | null
      share_null_reason: LeakageNullReason | null
      orders_with_discount: number
      by_coupon: null
      by_coupon_null_reason: 'no_coupon_source'
      previous_amount: number | null
    }
    failed_payments: {
      value: number
      orders: number
      attempts: number
      attempts_without_value: number
      recovered_value: number
      unrecovered_value: number
      recovered_rate: number | null
      recovered_rate_null_reason: LeakageNullReason | null
      share_of_gross: number | null
      share_null_reason: LeakageNullReason | null
      by_provider: { provider: string; failure_kind: string; value: number; recovered_value: number; attempts: number }[]
    } | null
    failed_payments_null_reason: LeakageNullReason | null
    draws: { type: 'draw'; at: string; source: 'draw_at' | 'end_at'; competition_id: string; name: string }[]
  }
  meta: RevenueLeakageMetaOut
}

export interface OrderRowOut {
  order_id: string
  external_order_id: string
  completed_at: string
  competitions: { competition_id: string; name: string }[]
  unsynced_competition_lines: number
  tickets: number
  currency: string
  gross: number
  net: number
  refunded: number
  payment_provider: string | null
  payment_method: string | null
  channel: OrderChannel
  customer_type: OrderCustomerType
  customer_id: string | null
}

export interface RevenueOrdersOut {
  data: {
    rows: OrderRowOut[]
    total: number
    sort: OrderSort
    order: SortOrder
    model: OrderAttributionModel
    limit: number
    next_cursor: string | null
  }
  meta: RevenueLeakageMetaOut
}

// --- what the page reads (camelCase, money in major units) ----------------------------------

/** The frame every answer carries: its currency and timezone, for formatting. */
export interface LeakageFrame {
  currency: string
  timezone: string
  previousUnavailable: PreviousUnavailable | null
}

export interface LifecyclePoint {
  day: number
  date: string
  revenue: number
  tickets: number
  cumulativeRevenue: number
  cumulativeSoldFraction: number | null
}

export interface LifecycleCurve {
  competitionId: string
  name: string
  category: string | null
  startAt: string
  drawAt: string | null
  drawSource: 'draw_at' | 'end_at' | null
  capacity: number | null
  soldOutDay: number | null
  points: LifecyclePoint[]
  truncated: boolean
  totals: { revenue: number; tickets: number }
}

export interface RevenueLifecycle extends LeakageFrame {
  basis: LeakageBasis
  align: LifecycleAlign
  curves: LifecycleCurve[]
  notAvailable: { competitionId: string; reason: LifecycleUnavailableReason }[]
}

export interface RefundReason {
  reason: string
  amount: number
  count: number
}

export interface RefundDay {
  day: string
  amount: number
  count: number
}

export interface FailedProvider {
  provider: string
  failureKind: string
  value: number
  recoveredValue: number
  attempts: number
}

export interface RevenueLeakage extends LeakageFrame {
  basis: LeakageBasis
  revenueGross: number
  refunds: {
    amount: number
    count: number
    rate: number | null
    rateNullReason: LeakageNullReason | null
    medianLagDays: number | null
    medianLagNullReason: LeakageNullReason | null
    lagRefunds: number
    byReason: RefundReason[]
    byDay: RefundDay[]
    previousAmount: number | null
    previousRate: number | null
    previousRateNullReason: LeakageNullReason | null
  }
  discounts: {
    amount: number
    shareOfGross: number | null
    shareNullReason: LeakageNullReason | null
    ordersWithDiscount: number
    /** Always null for now: no coupon source (`byCouponNullReason`). */
    byCoupon: null
    byCouponNullReason: 'no_coupon_source'
    previousAmount: number | null
  }
  /** Null with `failedPaymentsNullReason` (`needs_payment_events`): never 0. */
  failedPayments: {
    value: number
    orders: number
    attempts: number
    attemptsWithoutValue: number
    recoveredValue: number
    unrecoveredValue: number
    recoveredRate: number | null
    recoveredRateNullReason: LeakageNullReason | null
    shareOfGross: number | null
    shareNullReason: LeakageNullReason | null
    byProvider: FailedProvider[]
  } | null
  failedPaymentsNullReason: LeakageNullReason | null
  /** The range's draws: the refund timeline's shaded days. */
  draws: { at: string; competitionId: string; name: string; source: 'draw_at' | 'end_at' }[]
}

export interface OrderRow {
  orderId: string
  externalOrderId: string
  completedAt: string
  competitions: { competitionId: string; name: string }[]
  unsyncedCompetitionLines: number
  tickets: number
  /** The order's own: every money field of the row is in it, in major units. */
  currency: string
  gross: number
  net: number
  refunded: number
  paymentProvider: string | null
  paymentMethod: string | null
  channel: OrderChannel
  customerType: OrderCustomerType
  customerId: string | null
}

export interface RevenueOrders extends LeakageFrame {
  rows: OrderRow[]
  total: number
  sort: OrderSort
  order: SortOrder
  model: OrderAttributionModel
  nextCursor: string | null
}

export interface RevenueLeakageApi {
  revenueLifecycle(query: LifecycleQuery): Promise<Envelope<RevenueLifecycle>>
  revenueLeakage(query: LeakageQuery): Promise<Envelope<RevenueLeakage>>
  revenueOrders(query: OrdersQuery): Promise<Envelope<RevenueOrders>>
  /** Every matching order (up to `ORDERS_CSV_MAX_ROWS`) as CSV (`exports.create`); a refusal rejects with the API's error. */
  revenueOrdersCsv(query: OrdersQuery): Promise<CsvFile>
}

// --- query strings --------------------------------------------------------------------------

export function lifecycleSearch(query: LifecycleQuery, now: Date): string {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  params.set('basis', query.basis)
  params.set('align', query.align)
  for (const id of query.competitionIds.slice(0, LIFECYCLE_MAX_COMPETITIONS)) params.append('competition_ids', id)
  return params.toString()
}

export function leakageSearch(query: LeakageQuery, now: Date): string {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  params.set('basis', query.basis)
  params.set('compare', revenueCompare(query.filters))
  return params.toString()
}

/**
 * The orders route's parameters. `format=csv` asks for every matching row, so a CSV
 * leaves out the page's `limit` and `cursor`.
 */
export function ordersSearch(query: OrdersQuery, now: Date, csv = false): string {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  params.set('sort', query.sort)
  params.set('order', query.order)
  if (query.competitionId) params.set('competition_id', query.competitionId)
  if (query.channel) params.set('channel', query.channel)
  if (query.model) params.set('model', query.model)
  if (query.paymentProvider) params.set('payment_provider', query.paymentProvider)
  if (query.customerType) params.set('customer_type', query.customerType)
  if (query.minGrossMinor !== undefined && Number.isFinite(query.minGrossMinor) && query.minGrossMinor > 0) {
    params.set('min_gross', String(Math.trunc(query.minGrossMinor)))
  }
  if (csv) {
    params.set('format', 'csv')
  } else {
    if (query.limit !== undefined) params.set('limit', String(Math.min(ORDERS_MAX_LIMIT, Math.max(1, Math.trunc(query.limit)))))
    if (query.cursor) params.set('cursor', query.cursor)
  }
  return params.toString()
}

// --- mappers ----------------------------------------------------------------------------------

function frameFromWire(meta: RevenueLeakageMetaOut): LeakageFrame {
  return { currency: meta.currency, timezone: meta.timezone, previousUnavailable: meta.previous_unavailable }
}

export function lifecycleFromWire(body: RevenueLifecycleOut): RevenueLifecycle {
  const money = (minor: number) => majorUnits(minor, body.meta.currency)
  return {
    ...frameFromWire(body.meta),
    basis: body.data.basis,
    align: body.data.align,
    curves: body.data.competitions.map((curve) => ({
      competitionId: curve.competition_id,
      name: curve.name,
      category: curve.category,
      startAt: curve.start_at,
      drawAt: curve.draw_at,
      drawSource: curve.draw_source,
      capacity: curve.capacity,
      soldOutDay: curve.sold_out_day,
      truncated: curve.truncated,
      totals: { revenue: money(curve.totals.revenue), tickets: curve.totals.tickets },
      points: curve.points.map((point) => ({
        day: point.day,
        date: point.date,
        revenue: money(point.revenue),
        tickets: point.tickets,
        cumulativeRevenue: money(point.cumulative_revenue),
        cumulativeSoldFraction: point.cumulative_sold_fraction,
      })),
    })),
    notAvailable: body.data.not_available.map((entry) => ({ competitionId: entry.competition_id, reason: entry.reason })),
  }
}

export function leakageFromWire(body: RevenueLeakageOut): RevenueLeakage {
  const money = (minor: number) => majorUnits(minor, body.meta.currency)
  const moneyOrNull = (minor: number | null) => (minor === null ? null : money(minor))
  const { refunds, discounts, failed_payments: failed } = body.data
  return {
    ...frameFromWire(body.meta),
    basis: body.data.basis,
    revenueGross: money(body.data.revenue_gross),
    refunds: {
      amount: money(refunds.amount),
      count: refunds.count,
      rate: refunds.rate,
      rateNullReason: refunds.rate_null_reason,
      medianLagDays: refunds.median_lag_days,
      medianLagNullReason: refunds.median_lag_null_reason,
      lagRefunds: refunds.lag_refunds,
      byReason: refunds.by_reason.map((row) => ({ reason: row.reason_category, amount: money(row.amount), count: row.count })),
      byDay: refunds.by_day.map((row) => ({ day: row.day, amount: money(row.amount), count: row.count })),
      previousAmount: moneyOrNull(refunds.previous_amount),
      previousRate: refunds.previous_rate,
      previousRateNullReason: refunds.previous_rate_null_reason,
    },
    discounts: {
      amount: money(discounts.amount),
      shareOfGross: discounts.share_of_gross,
      shareNullReason: discounts.share_null_reason,
      ordersWithDiscount: discounts.orders_with_discount,
      byCoupon: null,
      byCouponNullReason: discounts.by_coupon_null_reason,
      previousAmount: moneyOrNull(discounts.previous_amount),
    },
    failedPayments: failed
      ? {
          value: money(failed.value),
          orders: failed.orders,
          attempts: failed.attempts,
          attemptsWithoutValue: failed.attempts_without_value,
          recoveredValue: money(failed.recovered_value),
          unrecoveredValue: money(failed.unrecovered_value),
          recoveredRate: failed.recovered_rate,
          recoveredRateNullReason: failed.recovered_rate_null_reason,
          shareOfGross: failed.share_of_gross,
          shareNullReason: failed.share_null_reason,
          byProvider: failed.by_provider.map((row) => ({
            provider: row.provider,
            failureKind: row.failure_kind,
            value: money(row.value),
            recoveredValue: money(row.recovered_value),
            attempts: row.attempts,
          })),
        }
      : null,
    failedPaymentsNullReason: body.data.failed_payments_null_reason,
    draws: body.data.draws.map((draw) => ({ at: draw.at, competitionId: draw.competition_id, name: draw.name, source: draw.source })),
  }
}

export function ordersFromWire(body: RevenueOrdersOut): RevenueOrders {
  return {
    ...frameFromWire(body.meta),
    rows: body.data.rows.map((row) => ({
      orderId: row.order_id,
      externalOrderId: row.external_order_id,
      completedAt: row.completed_at,
      competitions: row.competitions.map((competition) => ({ competitionId: competition.competition_id, name: competition.name })),
      unsyncedCompetitionLines: row.unsynced_competition_lines,
      tickets: row.tickets,
      currency: row.currency,
      gross: majorUnits(row.gross, row.currency),
      net: majorUnits(row.net, row.currency),
      refunded: majorUnits(row.refunded, row.currency),
      paymentProvider: row.payment_provider,
      paymentMethod: row.payment_method,
      channel: row.channel,
      customerType: row.customer_type,
      customerId: row.customer_id,
    })),
    total: body.data.total,
    sort: body.data.sort,
    order: body.data.order,
    model: body.data.model,
    nextCursor: body.data.next_cursor,
  }
}
